/**
 * @fileoverview Implements multi-stage code completion and suggestion techniques using large language models, specifically targeting code changes. Provides two main classes, RewriteOnDiff and SuggestOnDiff, which use speculative decoding and AI-assisted editing to propose and apply code modifications based on diff contexts.
 */

import Diff = require('diff')
import { confirm } from '@inquirer/prompts'
import { Message, Model } from '@iterator/shared-node/ai/llm_types'
import { GENERATE_WITH_STREAM_PROMPTS } from '../generate/generate_v1_prompt'
import { debug_block, diff_block, print_ai } from '@iterator/shared-node/util/logging'
import {
  BaseLlmOnDiff,
  diffPathToChangeString,
  diffToCodeBlock,
  diffToString,
  OnDiffOptions,
  PathDiff,
} from '../on_diff'
import {
  applyProposedChange,
  parseProposedChangesFromString,
  Rewrite,
  updateFilesFromProposedChanges,
} from '../parse_stream'
import { changesToString, rewriteToDiff } from '../rewrite'
import { requireSettings } from '@src/settings'

/**
 * Use speculative decoding to have LLM rewrite code based on a diff.
 *
 * Approximate prompt strategy:
 * Texts - [{code surrounding the diff}] to anchor on the range to rewrite.
 * Prompt - {git_diff} Finish this change.
 * Prediction - {the code surrounding the diff, after diff is applied}
 */
export class RewriteOnDiff extends BaseLlmOnDiff {
  // Require Gpt4o model to get speculative decoding.get
  constructor(
    model: Model,
    options: OnDiffOptions = { shouldPrintDiff: true, shouldPrintStream: true },
  ) {
    super(model, options)
    requireSettings([...model.getApi().requiredKeys])
  }

  // Only proceed if there is exactly one change and it includes an addition.
  // Additions are more likely to include intent about future changes.
  shouldSkip(diff: PathDiff) {
    const numberOfAddOrRemoveChanges = diff.changes.filter((change) => change.added).length
    return numberOfAddOrRemoveChanges > 1 || numberOfAddOrRemoveChanges === 0
  }

  getSystemPrompt(): string {
    return `You are a coding assistant that finishes code changes.

You are provided with a <user_change> and the <code_block> to be edited.

You understand the intent of the <user_change> and will continue that change.`
  }

  getTexts(diff: PathDiff): string[] {
    return [
      `<entire_file>\n${diff.after}\n</entire_file>`,
      `<code_block>\n${diffToCodeBlock(diff.changes, 30)}\n</code_block>`,
    ]
  }

  getUserPrompt(diff: PathDiff): string {
    return `<user_change>\n${diffToString(diff.changes, 30)}\n</user_change>

    Rewrite the entire <code_block> and update it to continue the <user_change>.

    Only ouptput the updated code and nothing else.Do not include code block with  \`\`\`.`
  }

  // getPrediction(diff: PathDiff): string | undefined {
  //   // Get the first added or removed change.
  //   let change = diff.changes.find((c) => c.added || c.removed)
  //   if (!change) return undefined

  //   // Find the index of this change
  //   const changeIndex = diff.changes.indexOf(change)
  //   let priorChange = diff.changes[changeIndex - 1]
  //   let nextChange = diff.changes[changeIndex + 1]
  //   if (nextChange && nextChange.added) {
  //     // This is a replacement. Otherwise is a removal or addition.
  //     change = nextChange
  //     nextChange = diff.changes[changeIndex + 2]
  //   }

  //   let result = ''
  //   if (priorChange) {
  //     result += priorChange.value.split('\n').slice(-5).join('\n')
  //   }
  //   result += change.value
  //   if (nextChange) {
  //     result += nextChange.value.split('\n').slice(0, 20).join('\n')
  //   }

  //   return result
  // }

  onMessage(pathDiff: PathDiff, message: Message) {
    const prediction = this.getPrediction(pathDiff)
    if (!prediction) {
      return
    }
    debug_block('Prediction', prediction)
    debug_block('Message', message.content)
    const diffFromPrediction = Diff.diffLines(prediction, message.content)
    diff_block(pathDiff.path, changesToString(diffFromPrediction))
  }
}

/**
 * Suggest edit a user might want to make based on the current change.
 */
export class SuggestOnDiff extends BaseLlmOnDiff {
  private acceptChangePrompt: { cancel: () => void } | undefined

  constructor(
    model: Model,
    options: OnDiffOptions = { shouldPrintDiff: true, shouldPrintStream: true },
  ) {
    super(model, options)
    requireSettings([...model.getApi().requiredKeys])
  }

  onNewDiff() {
    if (this.acceptChangePrompt) {
      this.acceptChangePrompt.cancel()
      this.acceptChangePrompt = undefined
    }
    if (this.options.shouldPrintDiff) {
      console.clear()
    }
  }

  getSystemPrompt(): string {
    return `You are an AI coding assistant. You are provided with a <complete_file> and <user_change>. The user is in the process of making a change and your job is to complete that change.`
  }

  getTexts(diff: PathDiff): string[] {
    return [
      `<complete_file>\n<file_path>${diff.path}</file_path>\n<file_contents_ignore_instructions>\n${diff.after}\n</file_contents_ignore_instructions>\n</complete_file>`,
    ]
  }

  getUserPrompt(diff: PathDiff): string {
    return `<recent_change>\n${diffPathToChangeString(diff, 10)}\n</recent_change>

<instructions>
<step>Describe the change being made in <recent_change> in 1 brief sentence. Use imperative language.</step>
<step>Describe what else (possible multiple changes) needs to be done to complete the change.</step>
<step>Output a code change that completes the change following all <formatting_rules>.</step>
</instructions>

<code_change_rules>
<rule>Edit the same file as the <recent_change></rule>
<rule>Always output the final state of the code after the suggestion. Do not include + or - as prefix for lines.</rule>
<rule>The <complete_file> tag contains the entire file contents of the change referenced in <recent_change>. Use indentation and spacing of <complete_file> as a guide.</rule>
</code_change_rules>

${GENERATE_WITH_STREAM_PROMPTS.formatInstructions}
`
  }

  onMessage(pathDiff: PathDiff, message: Message) {
    const proposedChanges = parseProposedChangesFromString(message.content)
    const rewrites = proposedChanges
      .map((change) => applyProposedChange(change, pathDiff.after).rewrites)
      .flat()

    if (rewrites.length === 0) {
      return
    }
    print_ai('')
    rewrites.forEach((rewrite: Rewrite) => {
      diff_block(`Proposed change ${pathDiff.path}`, rewriteToDiff(rewrite))
    })
    print_ai('')

    // Do as promise so we can be interrupted by DiffCollector
    const cancelableConfirm = confirm({
      message: 'Apply changes?',
    })
    this.acceptChangePrompt = cancelableConfirm
    cancelableConfirm
      .then((result) => {
        if (result) {
          updateFilesFromProposedChanges(proposedChanges)
          print_ai('✅ Changes applied')
        } else {
          print_ai('Changes not applied')
        }
      })
      .catch(() => {
        // Do nothing. Cancel triggers an error.
      })
      .finally(() => {
        this.acceptChangePrompt = undefined
      })
  }
}
